import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Camera, MapPin, Trophy } from "lucide-react";
import SportCategoryChip from "components/SportCategoryChip";
import {
  championshipLabel,
  eventChipLabel,
  formatEventDates,
  formatEventPlace,
} from "lib/events";

/**
 * Card de evento da AREA PUBLICA — usado pela home e pela pagina do campeonato.
 *
 * Vive fora das paginas porque as duas telas mostram exatamente a mesma grade;
 * duplicar o card faria a vitrine divergir conforme o caminho de entrada.
 */
export function EventCard({ event, lang, t, showChampionship = true }) {
  const place = formatEventPlace(event);
  return (
    <Link
      to={`/events/${event.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-neutral-100">
        {event.cover_url ? (
          <img
            src={event.cover_url}
            alt={event.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-neutral-300">
            <Camera size={36} />
          </div>
        )}
        {/* Evento esportivo mostra a MODALIDADE (Basquete, Vôlei…); os demais,
            o tipo. E o que o visitante procura ao bater o olho na grade. */}
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-neutral-700 backdrop-blur">
          {eventChipLabel(t, event)}
        </span>

        {/* Canto direito: a CATEGORIA da modalidade (chip com as cores do
            cadastro) e, abaixo dela, o status de rascunho/nao listado/arquivado
            — que so chega aqui para quem administra o acervo (o backend nao
            devolve esses eventos para o visitante anonimo). */}
        <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
          <SportCategoryChip chip={event.sport_category_chip} className="shadow-sm" />
          {event.status && event.status !== "published" && (
            <span className="rounded-full bg-neutral-900/85 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              {t(`event.status.${event.status}`)}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        {/* Na pagina do proprio campeonato o nome seria repetido em todo card. */}
        {showChampionship && event.championship && (
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-600">
            <Trophy size={12} />
            <span className="truncate">
              {championshipLabel(event.championship)}
            </span>
          </p>
        )}

        <h3 className="line-clamp-2 font-semibold text-neutral-900 group-hover:text-amber-600">
          {event.name}
        </h3>

        <p className="flex items-center gap-1.5 text-sm text-neutral-500">
          <CalendarDays size={14} />
          {formatEventDates(event, lang)}
        </p>

        {place && (
          <p className="flex items-center gap-1.5 text-sm text-neutral-500">
            <MapPin size={14} />
            {place}
          </p>
        )}

        <p className="mt-auto flex items-center gap-1.5 pt-2 text-sm font-medium text-amber-600">
          <Camera size={14} />
          {event.photo_count} {t("public.event.photos")}
        </p>
      </div>
    </Link>
  );
}

export function EventGridSkeleton() {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-2xl border border-neutral-200 bg-white"
        >
          <div className="aspect-[4/3] w-full animate-pulse bg-neutral-100" />
          <div className="space-y-2 p-4">
            <div className="h-4 w-3/4 animate-pulse rounded bg-neutral-100" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-neutral-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 px-6 py-20 text-center">
      <Camera size={32} className="text-neutral-300" />
      <p className="text-base font-semibold text-neutral-800">{title}</p>
      <p className="max-w-md text-sm text-neutral-500">{hint}</p>
      {action}
    </div>
  );
}

export default EventCard;
